import { useLingui } from '@lingui/react/macro'

/**
 * The projects list's columns: what exists, what each is called, how wide it
 * starts, and which of them can order or section the list.
 *
 * Mirrors `task-fields.ts` / `task-sort-keys.ts` deliberately, down to the
 * split between naming a column and drawing one. The two grids are NOT shared
 * code yet: the tasks grid is being built in parallel and is still moving, and
 * a premature extraction would have both of us editing one file. Keeping the
 * shapes identical is what makes that extraction mechanical later.
 *
 * These live only in the URL. Projects have no saved-view table, so unlike
 * tasks there is nothing to persist them into — a pasted link is the whole
 * sharing story.
 */

export type ProjectField =
  | 'client'
  | 'type'
  | 'usage'
  | 'status'
  | 'stage'
  | 'startDate'
  | 'endDate'
  | 'value'
  | 'owner'
  | 'city'
  | 'area'
  | 'tasks'
  | 'updatedAt'

/** Every column, in the order the field picker offers them. */
export const PROJECT_FIELDS: ProjectField[] = [
  'client',
  'type',
  'usage',
  'status',
  'stage',
  'startDate',
  'endDate',
  'value',
  'owner',
  'city',
  'area',
  'tasks',
  'updatedAt',
]

export const isProjectField = (value: string): value is ProjectField =>
  PROJECT_FIELDS.some((field) => field === value)

/** What a fresh board shows. Everything else is one click away in Colunas. */
export const DEFAULT_PROJECT_FIELDS: ProjectField[] = [
  'client',
  'type',
  'status',
  'stage',
  'endDate',
  'tasks',
]

/** Starting width in CSS pixels; a saved column width overrides it. */
export const PROJECT_FIELD_WIDTH: Record<ProjectField, number> = {
  area: 120,
  city: 150,
  client: 190,
  endDate: 140,
  owner: 170,
  stage: 180,
  startDate: 140,
  status: 160,
  tasks: 110,
  type: 180,
  updatedAt: 130,
  usage: 150,
  value: 150,
}

/**
 * Resize bounds, so a drag can never make a column unreadable or absurd.
 * The same numbers the tasks grid uses (`TASK_COLUMN_*` in `lib/taskViews`) —
 * two grids that look identical should not stop resizing at different points.
 */
export const PROJECT_COLUMN_MIN_WIDTH = 64
export const PROJECT_COLUMN_MAX_WIDTH = 720

/** The project name column: always first, always shown, pinned. */
export const PROJECT_NAME_COLUMN_WIDTH = 320

export type ProjectSortKey =
  | 'name'
  | 'client'
  | 'type'
  | 'usage'
  | 'status'
  | 'stage'
  | 'owner'
  | 'startDate'
  | 'endDate'
  | 'value'
  | 'city'
  | 'area'
  | 'tasks'
  | 'updatedAt'

export type ProjectSort = { direction: 'asc' | 'desc'; key: ProjectSortKey }

export const PROJECT_SORT_KEYS: ProjectSortKey[] = [
  'name',
  'client',
  'type',
  'usage',
  'status',
  'stage',
  'owner',
  'startDate',
  'endDate',
  'value',
  'city',
  'area',
  'tasks',
  'updatedAt',
]

/**
 * Name, ascending — a default you can SEE.
 *
 * It was `updatedAt` desc, which is a defensible order and an invisible one:
 * `updatedAt` is not one of the six default columns, so a fresh board carried
 * no caret anywhere and read as unsorted. An order nobody can point at is an
 * order nobody trusts.
 */
export const DEFAULT_PROJECT_SORT: ProjectSort = { direction: 'asc', key: 'name' }

export type ProjectGroupBy = 'none' | 'status' | 'type' | 'client' | 'owner'

export const PROJECT_GROUP_BYS: ProjectGroupBy[] = ['none', 'status', 'type', 'client', 'owner']

export const isProjectGroupBy = (value: string): value is ProjectGroupBy =>
  PROJECT_GROUP_BYS.some((group) => group === value)

/**
 * Stage is NOT a grouping here, and that is the same argument the board makes:
 * execution templates are per project type, so a stage key means one thing on
 * one spine and something else on another. The kanban is where stage sections
 * the work, because there each board is already one template.
 */
export const DEFAULT_PROJECT_GROUP_BY: ProjectGroupBy = 'none'

/**
 * The sort key a column orders by.
 *
 * EVERY column has one. Usage, city and area were left out at first, on the
 * grounds that they had "no order anyone would agree on" — but a city sorts
 * alphabetically, an area numerically, and a usage by its own catalog order,
 * exactly as type already did. The only genuinely unorderable column would be
 * one whose values have no relation to each other, and the grid has none.
 */
export const PROJECT_SORT_KEY_OF: Record<ProjectField, ProjectSortKey> = {
  area: 'area',
  city: 'city',
  client: 'client',
  endDate: 'endDate',
  owner: 'owner',
  stage: 'stage',
  startDate: 'startDate',
  status: 'status',
  tasks: 'tasks',
  type: 'type',
  updatedAt: 'updatedAt',
  usage: 'usage',
  value: 'value',
}

/** The grouping a column offers, or `undefined` when it cannot section a list. */
export const PROJECT_GROUP_BY_OF: Partial<Record<ProjectField, ProjectGroupBy>> = {
  client: 'client',
  owner: 'owner',
  status: 'status',
  type: 'type',
}

export const useProjectFieldLabels = (): Record<ProjectField, string> => {
  const { t } = useLingui()
  return {
    area: t`Area`,
    city: t`City`,
    client: t`Client`,
    endDate: t`Due date`,
    owner: t`Owner`,
    stage: t`Stage`,
    startDate: t`Start date`,
    status: t`Status`,
    tasks: t`Tasks`,
    type: t`Type`,
    updatedAt: t`Updated`,
    usage: t`Usage`,
    value: t`Value`,
  }
}

export const useProjectGroupLabels = (): Record<ProjectGroupBy, string> => {
  const { t } = useLingui()
  return {
    client: t`Client`,
    none: t`No grouping`,
    owner: t`Owner`,
    status: t`Status`,
    type: t`Type`,
  }
}
